<template>
  <v-card>
    <v-card-title>
      <strong class="headline"> {{ formTitle }}</strong>
    </v-card-title>
    <v-card-text>
      <v-form>
        <v-layout row wrap>
          <v-flex sm12>
            <v-radio-group row v-model="selectedTestimonialType">
              <v-radio
                v-for="(item, index) in nameTestimonialTypes"
                :key="`type-${index}`"
                :label="item"
                :value="item"
              />
            </v-radio-group>
          </v-flex>
          <v-flex xs5>
          <strong>Name:</strong>
          <content-field
            label="Name"
            v-model="testimonialsForm.employeeName"
            :error="$v.testimonialsForm.employeeName"
          />
        </v-flex>
        <v-spacer />
        <v-flex xs5>
          <strong>Company:</strong>
          <content-field
            label="Company"
            v-model="testimonialsForm.companyName"
            :error="$v.testimonialsForm.companyName"
          />
        </v-flex>
        <v-flex xs5>
          <strong>Job title:</strong>
          <content-field
            label="Job title"
            v-model="testimonialsForm.jobTitle"
            :error="$v.testimonialsForm.jobTitle"
          />
        </v-flex>
        <v-spacer />
        <v-flex xs5>
          <strong>Testimonials type:</strong>
          <v-select
            :items="testimonialTypesListFiltered"
            label="Testimonials type"
            :error="$v.testimonialsForm.testimonialTypeId.$invalid && $v.testimonialsForm.testimonialTypeId.$dirty"
            single-line
            item-value="id"
            item-text="name"
            v-model="testimonialsForm.testimonialTypeId"
          />
        </v-flex>

          <!-- IMAGE TESTIMONIAL FIELDS -->

          <v-layout row v-if="checkTestimonialType('Image testimonial')">
            <v-flex xs5>
              <v-card
                :class="{ errorBorder: isUploadedImg }"
                width="250">
                <div class="grey lighten-2">
                  <v-card-media
                  height="100"
                  :src="setImageUrl"/>
                </div>
                <v-card-title
                  v-if="isUploadedImg"
                >
                  <h3 :class="{ errorTitle: isUploadedImg }">
                    This field is required
                  </h3>
                  </v-card-title>
                <v-card-title v-if="!isUploadedImg"><h3>Photo</h3></v-card-title>
                <v-card-actions>
                  <v-btn flat color="success" @click="toggleGalleriesList">Choose</v-btn>
                  <v-btn flat color="error"
                    @click="resetImage">Reset
                  </v-btn>
                </v-card-actions>
            </v-card>
            <div>
              <v-dialog v-model="modalGallery">
                <galleries-list
                  :modalView="true"
                  @pickedImage="setImage"
              />
            </v-dialog>
          </div>
          </v-flex>

          <v-spacer />

          <v-flex xs5>
            <strong>Text of testimonial:</strong>
            <content-field
              label="Text of testimonial"
              multi-line
              v-model="testimonialsForm.testimonialBody"
              :error="$v.testimonialsForm.testimonialBody"
            />
          </v-flex>
        </v-layout>

        <v-layout row v-if="checkTestimonialType('Video testimonial')">
          <v-flex xs12>
            <strong :class="{ errorUrl: isActive }">Youtube video url:</strong>
            <content-field
              label="Youtube video url"
              :error="$v.testimonialsForm.youtubeVideoCode"
              v-model="testimonialsForm.youtubeVideoCode"
            />
          </v-flex>
        </v-layout>
        </v-layout>
      </v-form>
      <v-card-actions>
        <v-spacer/>
        <v-btn color="blue darken-1" flat @click.stop="close">Cancel</v-btn>
        <v-btn color="blue darken-1" flat @click.stop="save">Save</v-btn>
      </v-card-actions>
    </v-card-text>
  </v-card>
</template>

<script>
import { path, pathOr, clone, omit } from 'ramda'
import ContentField from '@/components/shared/ContentField/index.vue'
import GalleriesList from '@/components/pages/Gallery/GalleriesList'
import { required, maxLength, url } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import getTextFromHTMLStr from '@/utils/getTextFromHTMLStr'

export default {
  name: 'TestimonialsForm',
  components: {
    ContentField,
    GalleriesList
  },
  props: {
    isNewTestimonials: {
      type: Boolean,
      default: false
    },
    testimonialObject: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      testimonialsForm: {
        employeeName: '',
        companyName: '',
        jobTitle: '',
        testimonialTypeId: '',
        testimonialBody: '',
        youtubeVideoCode: '',
        galleryItemId: ''
      },
      isActive: false,
      isUploadedImg: false,
      selectedImage: null,
      selectedTestimonialType: 'Image testimonial',
      nameTestimonialTypes: ['Image testimonial', 'Video testimonial'],
      modalGallery: false
    }
  },
  validations () {
    const form = {
      testimonialsForm: {
        employeeName: { required },
        companyName: { required },
        jobTitle: { required },
        testimonialTypeId: { required },
        testimonialBody: {
          required,
          maxLength: maxLength(240)
        },
        youtubeVideoCode: { required },
        galleryItemId: { required }
      }
    }
    if (this.selectedTestimonialType === 'Image testimonial') {
      delete form.testimonialsForm.youtubeVideoCode
    } else {
      delete form.testimonialsForm.testimonialBody
      delete form.testimonialsForm.galleryItemId
    }
    return form
  },
  computed: {
    ...mapGetters([
      'testimonialTypesList'
    ]),
    testimonialTypesListFiltered() {
      return clone(this.testimonialTypesList).map(testimonialType => {
        testimonialType.name = getTextFromHTMLStr(testimonialType.name)
        return testimonialType
      })
    },
    formTitle () {
      return this.isNewTestimonials ? 'New Testimonials' : 'Edit testimonials'
    },
    setImageUrl() {
      return pathOr('', ['imagesUrls', 'originalImageUrl'], this.selectedImage)
    },
  },
  watch: {
    selectedImage (val) {
        this.testimonialsForm.galleryItemId = pathOr('', ['id'], val)
    }
  },
  methods: {
    ...mapActions([
      'createLastYearTestimonials',
      'updateLastYearTestimonials'
    ]),
    checkTestimonialType (type) {
      return this.selectedTestimonialType === type
    },
    close () {
      this.$emit('close')
    },
    resetImage () {
      this.selectedImage = null
    },
    setImage (img) {
      this.selectedImage = img
      this.toggleGalleriesList()
    },
    toggleGalleriesList () {
      this.isUploadedImg = false
      this.modalGallery = !this.modalGallery
    },
    async checkYoutubeDomain (date) {
      try {
        function findMatch () {
          const urlList = [
            'https://www.youtube.com',
            'https://youtu.be'
          ]
          return urlList.filter(url => date.youtubeVideoCode.indexOf(url) !== -1)
        }
        const result = findMatch ()
        return result.length ? await this.doCreateLastYearTestimonials(date) : this.isActive = true
      } catch (err) {
        throw err
      }
    },
    async doCreateLastYearTestimonials (newTestimonialsDate) {
      await this.createLastYearTestimonials(newTestimonialsDate)
      this.close()
      this.$bus.$emit('reloadTestimonialsTable')
    },
    async save () {
      this.$v.testimonialsForm.$touch()
      if (this.$v.testimonialsForm.$invalid) {
        this.isUploadedImg = this.$v.testimonialsForm.galleryItemId.$invalid
        return
      }
      let payload = clone(this.testimonialsForm)
      if (this.checkTestimonialType('Image testimonial')) {
        payload = omit(['youtubeVideoCode'], payload)
      } else {
        payload = omit(['testimonialBody', 'galleryItemId'], payload)
      }
      if (this.isNewTestimonials) {
        try {
          return payload.youtubeVideoCode ? await this.checkYoutubeDomain(payload) : await this.doCreateLastYearTestimonials(payload)
        } catch (err) {
          throw err
        }
      } else {
        try {
          await this.updateLastYearTestimonials(payload)
          this.close()
          this.$bus.$emit('reloadTestimonialsTable')
        } catch (err) {
          throw err
        }
      }
      // try {
      //   const requestMethod = this.isNewTestimonials ? this.createLastYearTestimonials : this.updateLastYearTestimonials
      //   requestMethod(payload)
      // } catch (err) {
      //   console.error(err)
      // } finally {
      //   this.close()
      //   this.$bus.$emit('reloadTestimonialsTable')
      // }
    }
  },
  created() {
    this.testimonialsForm = clone(this.testimonialObject)
    if (!this.isNewTestimonials) {
      this.testimonialsForm.galleryItemId = path(['galleryItem', 'id'], this.testimonialObject)
      if (this.testimonialsForm.galleryItemId) {
        this.selectedTestimonialType = 'Image testimonial'
        this.selectedImage = clone(path(['galleryItem'], this.testimonialObject))
      } else {
        this.selectedTestimonialType = 'Video testimonial'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.errorBorder {
  border: 1px solid red;
}

.errorTitle {
  color: red;
}

.errorUrl {
  color: red;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/Testimonials/TestimonialsList/testimonialsForm.vue